import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Lock, ArrowRight, CheckCircle2, ArrowLeft, Eye, EyeOff } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPasswordPage() {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (password.length < 6) {
      setError(t("auth.passwordTooShort"));
      return;
    }
    if (password !== confirmPassword) {
      setError(t("auth.passwordsDoNotMatch"));
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4"
        style={{ background: "radial-gradient(ellipse at center, #1a3a5c 0%, #0B1F3A 50%, #061020 100%)" }}>
        <Card className="w-full max-w-md border-border shadow-2xl">
          <CardContent className="pt-8 pb-8 text-center">
            <p className="text-destructive font-medium mb-4">{t("auth.invalidResetLink")}</p>
            <Link to="/login">
              <Button variant="outline" className="h-11 rounded-xl">
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t("auth.backToLogin")}
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4"
      style={{ background: "radial-gradient(ellipse at center, #1a3a5c 0%, #0B1F3A 50%, #061020 100%)" }}>
      <Card className="w-full max-w-md border-border shadow-2xl" data-testid="reset-password-page">
        <CardHeader className="space-y-1 text-center pb-2">
          <div className="flex justify-center mb-6">
            <span className="text-3xl font-black tracking-tight bg-gradient-to-r from-[#0B1F3A] to-[#3A6D8C] bg-clip-text text-transparent dark:from-white dark:to-slate-300">
              Ellalink
            </span>
          </div>
          <CardTitle className="text-2xl font-bold">{t("auth.resetPassword")}</CardTitle>
          <CardDescription className="text-base">{t("auth.resetPasswordDesc")}</CardDescription>
        </CardHeader>
        <CardContent className="pt-6">
          {success ? (
            <div className="text-center space-y-4" data-testid="reset-password-success">
              <div className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto">
                <CheckCircle2 className="h-8 w-8 text-emerald-500" />
              </div>
              <h3 className="text-lg font-semibold">{t("auth.passwordResetSuccess")}</h3>
              <p className="text-sm text-muted-foreground">{t("auth.passwordResetSuccessDesc")}</p>
              <Link to="/login">
                <Button className="mt-4 h-11 rounded-xl" data-testid="go-to-login-btn">
                  {t("auth.login")}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-xl text-destructive text-sm">
                  {error}
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium">{t("auth.newPassword")}</Label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                  <Input id="password" type={showPassword ? "text" : "password"} placeholder="••••••••" value={password}
                    onChange={(e) => setPassword(e.target.value)} className="h-12 pl-12 pr-12 rounded-xl text-base" required data-testid="reset-password-input" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-sm font-medium">{t("auth.confirmPassword")}</Label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                  <Input id="confirmPassword" type={showPassword ? "text" : "password"} placeholder="••••••••" value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)} className="h-12 pl-12 rounded-xl text-base" required data-testid="reset-confirm-password-input" />
                </div>
              </div>
              <Button type="submit" className="w-full h-12 text-base font-semibold rounded-xl shadow-lg shadow-primary/30"
                disabled={loading} data-testid="reset-submit-btn">
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    {t("auth.resetting")}
                  </span>
                ) : (
                  <>{t("auth.resetPassword")}<ArrowRight className="ml-2 h-5 w-5" /></>
                )}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
